
import React from "react";
import { Icon, type IconName } from "../Icon";

export interface TabItem<T extends string = string> {
  value: T;
  label: React.ReactNode;
  iconName?: IconName;
}

interface TabsProps<T extends string = string> {
  tabs: TabItem<T>[];
  value: T;
  onChange: (v: T) => void;
  /** Zusätzliche Klassen auf dem äußeren Tab-Leisten-Container. */
  className?: string;
}

/**
 * Underline-Tabs (kontrolliert): aktive Leiste = Brand-Border + heller Text,
 * inaktiv = transparente Border + sekundärer Text mit Hover. Theme-aware über
 * `border-primary` (im Dark aufgehellt) + `text-content*`. Der Inhalt der Tabs
 * wird vom Consumer gerendert (wie bei `Segmented`), nur die Kopf-Leiste hier.
 */
export function Tabs<T extends string = string>({ tabs, value, onChange, className = "" }: TabsProps<T>) {
  return (
    <div className={`border-b border-line ${className}`.trim()} role="tablist">
      <div className="-mb-px flex gap-1 overflow-x-auto">
        {tabs.map((t) => {
          const active = t.value === value;
          return (
            <button
              key={t.value}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => onChange(t.value)}
              className={`inline-flex shrink-0 items-center gap-2 border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
                active
                  ? "border-primary text-content"
                  : "border-transparent text-content-2 hover:border-line-strong hover:text-content"
              }`}
            >
              {t.iconName && <Icon name={t.iconName} size={14} />}
              {t.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default Tabs;
